// Sync (spec §8.4). Feature 008 pulled a full dataset and never drained the
// outbox; feature 009 completes it — push, delta pull, and Background Sync.
//
// ⚠️  The manual "Sync now" button stays ALWAYS VISIBLE even now that Background
// Sync exists. It is unsupported on Safari/iOS, so §8.4 requires the fallback
// regardless — and at a gate, an unattended mechanism nobody can see is one
// nobody trusts. A volunteer who cannot make sync happen on demand will restart
// the app instead, which is worse.
import { fetchDataset, pushScans } from './api.js';
import {
  replaceTickets,
  setMeta,
  countTickets,
  listOutbox,
  clearOutbox,
  applyDelta,
  countOutbox,
} from './db.js';

/**
 * Store a freshly downloaded dataset.
 *
 * Shared by provisioning and refresh so there is exactly one place that decides
 * what "the device is prepared" means.
 */
export async function storeDataset(payload, extra = {}) {
  await replaceTickets(payload.tickets);
  await setMeta({
    eventId: payload.event.id,
    eventName: payload.event.name,
    publicKey: payload.publicKey,
    lastSyncedAt: payload.syncedAt ?? new Date().toISOString(),
    ticketCount: payload.tickets.length,
    ...extra,
  });
  return payload.tickets.length;
}

/**
 * Push what this device has seen, then take back what it missed — in ONE call
 * (feature 009, research R5).
 *
 * A gate's connectivity window may be seconds long, and two round trips is two
 * chances to half-succeed.
 *
 * ⚠️  Only scans the server CONFIRMED are cleared. A scan it could not accept
 * stays queued and stays counted, rather than disappearing because the ones
 * beside it succeeded (FR-010).
 */
export async function flushOutbox() {
  const pending = await listOutbox();
  const since = await import('./db.js').then((m) => m.getMeta('lastSyncedAt'));
  const result = await pushScans(
    pending.map(({ id, registrationId, scannedAt, outcome, mode }) => ({
      id,
      registrationId,
      scannedAt,
      outcome,
      mode: mode ?? 'offline',
    })),
    since ?? undefined,
  );

  await clearOutbox(result.accepted);

  /**
   * ⚠️  `full` means the server refused our `since` — a clock it could not trust
   * — and the only safe response is a complete re-download, because a partial
   * delta with gaps leaves tickets absent and absence is §8.3's ADMIT condition.
   *
   * ⚠️  It is reported, NOT acted on here. This used to call `syncNow()`, which
   * calls `flushOutbox()`, which pushes the SAME stale `since` — the `full`
   * branch never updates `lastSyncedAt` — so the server answers `full` again and
   * the two functions recurse until the stack gives out. The outbox is empty by
   * the second pass, so it spins on empty pushes: a device that regained signal
   * would hang rather than sync.
   *
   * Nothing is lost by only reporting it: `syncNow` ALREADY does an
   * unconditional `fetchDataset` immediately after this returns, which is
   * exactly the complete re-download this case needs.
   */
  const needsFullResync = result.delta?.full === true;

  if (needsFullResync) {
    // Deliberately nothing. The caller re-downloads — see above.
  } else if (result.delta?.changed?.length) {
    await applyDelta(result.delta.changed);
    await setMeta({ lastSyncedAt: result.delta.syncedAt, ticketCount: await countTickets() });
  } else if (result.delta?.syncedAt) {
    await setMeta({ lastSyncedAt: result.delta.syncedAt });
  }

  return {
    pushed: result.accepted.length,
    rejected: result.rejected ?? [],
    stillQueued: await countOutbox(),
    /**
     * ⚠️  A caller that does NOT follow this with a full `fetchDataset` must do
     * one when this is true. `syncNow` does; anything calling `flushOutbox`
     * directly — a Background Sync handler, were one ever added — would be
     * working from a dataset with holes in it, and a hole reads as "unknown
     * ticket", which §8.3 admits.
     */
    needsFullResync,
  };
}

export async function syncNow() {
  // Push before pulling: the delta we take back should reflect what this device
  // has already told the server, or a second gate may not yet know about our
  // admissions.
  try {
    await flushOutbox();
  } catch {
    // No network, or the push failed. The full pull below may still succeed, and
    // the outbox is durable — nothing is lost by carrying on.
  }
  const payload = await fetchDataset();
  // Only overwrite the device's identity when the response actually carried it.
  // Spreading `undefined` would blank the gate name already on file and leave
  // the status bar reading "Gate — device" at a gate that has a name.
  const extra = {};
  if (payload.device?.gateName !== undefined) extra.gateName = payload.device.gateName;
  if (payload.device?.label !== undefined) extra.label = payload.device.label;
  /**
   * ⚠️  Refreshed on EVERY sync, never fixed at provisioning (feature 009, R4).
   * A second device appearing mid-event is exactly when the race starts; a
   * device still believing it is alone would validate locally all day and this
   * feature would silently do nothing at the one event that needed it.
   */
  if (payload.multiDevice !== undefined) extra.multiDevice = payload.multiDevice;
  const count = await storeDataset(payload, extra);
  return { ticketCount: count, syncedAt: payload.syncedAt };
}

/**
 * Is this device actually able to give trustworthy verdicts? (FR-033, R14)
 *
 * The failure being guarded against is specific and severe: with an empty
 * ticket store and no public key, every code presented — genuine or forged —
 * looks like "valid signature, not in my list", which is the ADMIT path. A
 * device whose storage was evicted would wave through everybody. Refusing to
 * show a scan screen at all is the only safe answer.
 */
export async function readiness() {
  const [token, publicKey, eventId, expected, tickets] = await Promise.all([
    import('./db.js').then((m) => m.getMeta('deviceToken')),
    import('./db.js').then((m) => m.getMeta('publicKey')),
    import('./db.js').then((m) => m.getMeta('eventId')),
    import('./db.js').then((m) => m.getMeta('ticketCount')),
    countTickets(),
  ]);

  if (!token) return { ready: false, reason: 'not_provisioned' };
  if (!publicKey || !eventId) return { ready: false, reason: 'data_lost' };
  // LOSS is the thing being detected, not emptiness. `ticketCount` records what
  // the last sync delivered, so "it delivered rows and none are left" is the
  // evicted-storage case; "it delivered none" is an event that has simply sold
  // nothing this device has heard about, and forged codes still cannot pass —
  // the signature check upstream needs the event key, which is present here.
  if (tickets === 0 && (expected ?? 0) > 0) return { ready: false, reason: 'data_lost' };
  return { ready: true };
}


/**
 * Background Sync (feature 009, FR-008).
 *
 * ⚠️  REGISTERING THE TAG IS ALL THIS DOES, AND NOTHING HANDLES IT YET. The PWA
 * builds with `vite-plugin-pwa`'s GENERATED Workbox worker — no `injectManifest`,
 * no custom `srcDir` — and a generated worker has no `sync` listener. So on
 * Chrome/Android the browser accepts the registration and then wakes a worker
 * that does nothing with it; on iOS the API is absent entirely.
 *
 * It is kept because it is the correct call site the day a real handler exists,
 * and because the registration itself is harmless. What must NOT happen is
 * anybody reading this and believing a closed app drains its outbox — it does
 * not, and re-registering the tag after every scan would not change that.
 *
 * ⚠️  WHAT ACTUALLY DRAINS THE QUEUE while the app is open is the `online`
 * listener in `main.js`, plus the always-visible "Sync now". That covers a gate
 * with the app in the foreground, which is where a gate device spends the event.
 * The uncovered case is narrow and real: scans queued, app closed, signal
 * returns, app never reopened before the organizer reads their numbers.
 *
 * Making that case work needs `strategies: 'injectManifest'` and a worker with
 * a `sync` handler calling `flushOutbox` — a change to the build, not a call
 * added here.
 */
export async function registerBackgroundSync() {
  try {
    const registration = await navigator.serviceWorker?.ready;
    if (!registration?.sync) return false;
    await registration.sync.register('kontriticket-outbox');
    return true;
  } catch {
    // Unsupported, or permission refused. Neither is a problem worth surfacing:
    // the manual button covers it.
    return false;
  }
}
